<template>
	<div class="battlefield-header" :class="[skin, supplyClass]">
		<div class="battlefield-header__heading">
			{{ translate("ui.battlefield") }}
		</div>
		<div class="battlefield-header__content">{{ contentText }}</div>
		<AppIconButton
			class="info-button"
			:type="IconTypes.INFO"
			:size="32"
			@click="onClick"
		/>
	</div>
</template>

<script lang="ts" setup>
import AppIconButton from "@/components/AppIconButton.vue";
import { PopupType, usePopupStore } from "@/store/usePopupStore";
import { IconTypes, SkinTypes } from "@/types/ui";
import { translate } from "@/utils/translate.ts";
import { computed, markRaw } from "vue";
import FightIntroPopup from "./FightIntroPopup.vue";

const {
	skin = "neutral",
	supplyLimitValue = 0,
	supplyLimitMax = 0,
} = defineProps<{
	skin?: "friendly" | "enemy" | "neutral";
	supplyLimitValue?: number;
	supplyLimitMax?: number;
}>();

const contentText = computed((): string => {
	if (skin === "neutral") {
		return `${translate("ui.supply_limit")}: ${supplyLimitValue}/${supplyLimitMax}`;
	}
	if (skin === "friendly") return translate("ui.friendly_unit");
	if (skin === "enemy") return translate("ui.enemy_units");
	return "";
});

const supplyClass = computed((): string => {
	if (skin === "neutral" && supplyLimitMax === 0) return "supply-empty";
	return "";
});

const onClick = (): void => {
	usePopupStore().showPopup({
		type: PopupType.INFO,
		skin: SkinTypes.DARK,
		isPaddingless: true,
		isCentered: true,
		isPriority: true,
		height: "auto",
		width: 23.875,
		component: markRaw(FightIntroPopup),
	});
};
</script>

<style lang="scss" scoped>
.battlefield-header {
	box-shadow: inset 0 0 0.5rem 0 rgba(0, 0, 0, 0.3);
	border-width: 0.075rem;
	border-style: solid;
	border-image-source: linear-gradient(to top, #bd9032, #f0d386);
	border-image-slice: 1;
	background-size: cover;
	height: 3.25rem;
	@include flex-col-center;
	position: relative;
	gap: 0.25rem;
	width: 100%;

	&__heading {
		color: #f4d278;
		font-weight: 800;
		font-size: 1.1563rem;
		line-height: 1;
		text-shadow: 0.0437rem 0.0437rem 0 rgba(0, 0, 0, 0.6);
	}

	&__content {
		color: #fff3dd;
		color: rgb(255, 243, 221);
		font-size: 0.8125rem;
		line-height: 1;
		text-shadow: 0.0437rem 0.0437rem 0 rgba(0, 0, 0, 0.6);
	}

	&.neutral {
		background-image: url($path-images + "backgrounds/fight-system/battlefield-bg-grey.avif");
		.battlefield-header__heading {
			color: rgb(255, 243, 221);
		}
	}
	&.friendly {
		background-image: url($path-images + "backgrounds/fight-system/battlefield-bg-blue.avif");
	}
	&.enemy {
		background-image: url($path-images + "backgrounds/fight-system/battlefield-bg-red.avif");
	}
	&.supply-empty {
		border: solid 1.2px rgb(255, 61, 61);

		.battlefield-header__content {
			color: rgb(255, 61, 61);
		}
	}
}
.info-button {
	position: absolute;
	right: 0.5rem;
}
</style>
